// Copyright (c) 2024. UpCircle Technology AG.
// All rights reserved. Redistribution in source and/or binary forms,
// with or without modification, is not permitted.

import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import logo_circle from '../assets/logos/logo_uc_blues_on_white.svg';
import { useNavigate } from 'react-router-dom';
import {
  signInWithEmailAndPassword,
  GoogleAuthProvider,
  OAuthProvider,
  signInWithPopup,
  signOut,
  linkWithCredential,
} from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, firestore } from '../context/firebase';

export const Login = () => {
  const { t, i18n } = useTranslation();
  const [err, setErr] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [pendingCred, setPendingCred] = useState(null);
  const [pendingCredEmail, setPendingCredEmail] = useState('');
  const [linkMessage, setLinkMessage] = useState('');
  const [emailValue, setEmailValue] = useState('');
  const navigate = useNavigate();

  const socialSignInActive = useRef(false);

  useEffect(() => {
    const browserLang = navigator.language?.startsWith('de') ? 'de' : 'en';
    if (i18n.language !== browserLang) i18n.changeLanguage(browserLang);
  }, [i18n]);

  // Reset loading state when page becomes visible after a social sign-in popup.
  // On some mobile browsers, signInWithPopup's promise never settles if the user
  // closes the popup or navigates back without completing auth.
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && socialSignInActive.current) {
        setTimeout(() => {
          if (socialSignInActive.current) {
            socialSignInActive.current = false;
            setIsLoading(false);
          }
        }, 2000);
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  const verifyUserDoc = async (user) => {
    const userDoc = await getDoc(doc(firestore, 'users', user.uid));
    if (!userDoc.exists()) {
      await signOut(auth);
      setErr(t('auth_no_account'));
      return false;
    }
    return true;
  };

  const tryLinkPendingCred = async (user) => {
    if (!pendingCred) return;

    if (pendingCredEmail && user.email !== pendingCredEmail) {
      const mismatchError = new Error('Signed in email does not match pending credential email');
      mismatchError.code = 'auth/link-email-mismatch';
      throw mismatchError;
    }

    try {
      await linkWithCredential(user, pendingCred);
      setPendingCred(null);
      setPendingCredEmail('');
      setLinkMessage('');
    } catch (linkError) {
      const failedError = new Error('Failed to link pending OAuth credential');
      failedError.code = 'auth/link-failed';
      failedError.cause = linkError;
      throw failedError;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErr(null);
    setLinkMessage('');
    const formData = new FormData(e.currentTarget);
    const email = formData.get('email');
    const password = formData.get('password');

    setIsLoading(true);
    try {
      const result = await signInWithEmailAndPassword(auth, email, password);
      await tryLinkPendingCred(result.user);
      if (!(await verifyUserDoc(result.user))) return;
      navigate('/');
    } catch (error) {
      if (error.code === 'auth/link-email-mismatch') {
        await signOut(auth);
        setErr(t('auth_link_email_mismatch'));
        setLinkMessage(t('auth_link_existing_account'));
        setEmailValue(pendingCredEmail);
      } else if (error.code === 'auth/link-failed') {
        await signOut(auth);
        setErr(t('auth_link_failed'));
      } else if (error.code === 'auth/network-request-failed') {
        setErr(t('auth_network_error'));
      } else {
        setErr(t('login_wrong_credentials'));
      }
      console.error(error.code);
      console.error(error.message);
    } finally {
      socialSignInActive.current = false;
      setIsLoading(false);
    }
  };

  const handleSocialSignInError = (err) => {
    switch (err.code) {
      case 'auth/popup-closed-by-user':
      case 'auth/cancelled-popup-request':
        break;
      case 'auth/popup-blocked':
        setErr(t('auth_popup_blocked'));
        break;
      case 'auth/network-request-failed':
        setErr(t('auth_network_error'));
        break;
      default:
        console.error(err.code, err.message);
        setErr(t('auth_no_account'));
    }
  };

  const handleGoogleSignIn = async () => {
    setErr(null);
    setLinkMessage('');
    setIsLoading(true);
    socialSignInActive.current = true;
    try {
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);
      socialSignInActive.current = false;
      await tryLinkPendingCred(result.user);
      if (!(await verifyUserDoc(result.user))) return;
      navigate('/');
    } catch (err) {
      if (err.code === 'auth/account-exists-with-different-credential') {
        const credential = GoogleAuthProvider.credentialFromError(err);
        if (credential) {
          const email = err.customData?.email;
          setPendingCred(credential);
          setPendingCredEmail(email || '');
          setLinkMessage(t('auth_link_existing_account'));
          if (email) setEmailValue(email);
        } else {
          handleSocialSignInError(err);
        }
      } else {
        handleSocialSignInError(err);
      }
    } finally {
      socialSignInActive.current = false;
      setIsLoading(false);
    }
  };

  const handleMicrosoftSignIn = async () => {
    setErr(null);
    setLinkMessage('');
    setIsLoading(true);
    socialSignInActive.current = true;
    try {
      const provider = new OAuthProvider('microsoft.com');
      provider.setCustomParameters({ prompt: 'select_account' });
      const result = await signInWithPopup(auth, provider);
      socialSignInActive.current = false;
      await tryLinkPendingCred(result.user);
      if (!(await verifyUserDoc(result.user))) return;
      navigate('/');
    } catch (err) {
      if (err.code === 'auth/account-exists-with-different-credential') {
        const credential = OAuthProvider.credentialFromError(err);
        if (credential) {
          const email = err.customData?.email;
          setPendingCred(credential);
          setPendingCredEmail(email || '');
          setLinkMessage(t('auth_link_existing_account'));
          if (email) setEmailValue(email);
        } else {
          handleSocialSignInError(err);
        }
      } else {
        handleSocialSignInError(err);
      }
    } finally {
      socialSignInActive.current = false;
      setIsLoading(false);
    }
  };

  const inputClass =
    'mt-1 block h-10 w-full rounded-md border border-gray-300 bg-gray-50 px-3 py-0 text-base sm:text-sm text-gray-900 font-normal outline-none appearance-none box-border font-[inherit] focus:border-[#3d71b2] focus:ring-1 focus:ring-[#6b94c9]';

  const socialBtnClass =
    'flex items-center justify-center gap-3 w-full min-h-10 rounded-lg border border-gray-300/60 bg-white px-4 py-2 text-sm font-medium text-gray-700 font-[inherit] border-solid cursor-pointer transition-all duration-300 hover:bg-gray-50 hover:shadow-[1px_1px_6px_0px] hover:shadow-black/10 disabled:opacity-50 disabled:pointer-events-none outline-0';

  return (
    <div className='min-h-dvh bg-[#16192c] flex flex-col items-center justify-center px-4 py-6 box-border antialiased'>
      <div className='bg-white rounded-xl p-6 sm:p-10 w-full max-w-[22rem] sm:max-w-sm flex flex-col items-center box-border'>
        <img className='w-12 h-12 sm:w-16 sm:h-16 mb-3 sm:mb-4' src={logo_circle} alt='UpCircle logo' />
        <h1 className='text-base sm:text-lg font-semibold text-[#101828] mt-2 mb-1'>
          <span className='text-[#3d71b2]'>UpCircle™</span> Go!
        </h1>
        <p className='text-sm text-gray-500 mt-1 mb-6'>{t('login')}</p>

        {linkMessage && (
          <p className='w-full text-sm text-amber-600 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-4 text-center'>
            {linkMessage}
          </p>
        )}

        <form onSubmit={handleSubmit} className='w-full flex flex-col gap-4'>
          <div>
            <label htmlFor='login-email' className='block text-sm font-medium text-gray-700 font-[inherit]'>
              {t('auth_email')}
            </label>
            <input
              id='login-email'
              name='email'
              type='email'
              placeholder='name@company.com'
              className={inputClass}
              disabled={isLoading}
              value={emailValue}
              onChange={(e) => setEmailValue(e.target.value)}
            />
          </div>

          <div>
            <label htmlFor='login-password' className='block text-sm font-medium text-gray-700 font-[inherit]'>
              {t('auth_password')}
            </label>
            <input
              id='login-password'
              name='password'
              type='password'
              placeholder='••••••••'
              className={inputClass}
              disabled={isLoading}
            />
          </div>

          <button
            type='submit'
            className='w-full h-10 rounded-lg border-none bg-[#3d71b2] text-white font-medium text-sm font-[inherit] hover:bg-[#244a82] transition-colors cursor-pointer disabled:opacity-50'
            disabled={isLoading}
          >
            {t('login')}
          </button>

          {err && <p className='text-sm text-red-500 text-center'>{err}</p>}
        </form>

        {/* Divider */}
        <div className='flex items-center gap-3 w-full my-5'>
          <div className='flex-1 border-t border-gray-200' />
          <span className='text-sm text-gray-400'>{t('auth_or')}</span>
          <div className='flex-1 border-t border-gray-200' />
        </div>

        {/* Social sign-in buttons */}
        <div className='w-full flex flex-col gap-2.5'>
          <button type='button' onClick={handleGoogleSignIn} disabled={isLoading} className={socialBtnClass}>
            <svg viewBox='0 0 24 24' width='18' height='18' aria-hidden='true'>
              <path
                d='M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z'
                fill='#4285F4'
              />
              <path
                d='M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z'
                fill='#34A853'
              />
              <path
                d='M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z'
                fill='#FBBC05'
              />
              <path
                d='M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z'
                fill='#EA4335'
              />
            </svg>
            {t('auth_sign_in_google')}
          </button>

          <button type='button' onClick={handleMicrosoftSignIn} disabled={isLoading} className={socialBtnClass}>
            <svg width='18' height='18' viewBox='0 0 21 21' aria-hidden='true'>
              <rect x='1' y='1' width='9' height='9' fill='#F25022' />
              <rect x='11' y='1' width='9' height='9' fill='#7FBA00' />
              <rect x='1' y='11' width='9' height='9' fill='#00A4EF' />
              <rect x='11' y='11' width='9' height='9' fill='#FFB900' />
            </svg>
            {t('auth_sign_in_microsoft')}
          </button>
        </div>
      </div>

      <div className='mt-4 sm:mt-6 mb-0 flex items-center gap-3 text-xs sm:text-sm text-gray-500'>
        <span>UpCircle Technology AG &middot; {new Date().getFullYear()}</span>
        <button
          type='button'
          onClick={() => {
            const next = i18n.language === 'de' ? 'en' : 'de';
            i18n.changeLanguage(next);
          }}
          style={{
            background: 'none',
            border: 'none',
            padding: 0,
            appearance: 'none',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            lineHeight: 1,
          }}
          className='text-gray-400 hover:text-gray-300 cursor-pointer font-[inherit] text-xs sm:text-sm outline-none'
        >
          <svg
            width='14'
            height='14'
            viewBox='0 0 24 24'
            fill='none'
            stroke='currentColor'
            strokeWidth='1.5'
            strokeLinecap='round'
            strokeLinejoin='round'
            aria-hidden='true'
            style={{ display: 'block', flexShrink: 0 }}
          >
            <circle cx='12' cy='12' r='10' />
            <path d='M2 12h20' />
            <path d='M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z' />
          </svg>
          {i18n.language === 'de' ? 'DE' : 'EN'}
        </button>
      </div>
    </div>
  );
};

export default Login;
